import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "react-toastify";
import Button from "../../components/Button";
import Card from "../../components/Card";
import Input from "../../components/Input";
import LoadingSpinner from "../../components/LoadingSpinner";
import { api } from "../../services/api";

export default function VerifyEmail() {
  const [searchParams] = useSearchParams();
  const [status, setStatus] = useState("verifying");
  const [message, setMessage] = useState("");
  const [redirecting, setRedirecting] = useState(false);
  const [resendEmail, setResendEmail] = useState(
    searchParams.get("email") || ""
  );
  const [resendLoading, setResendLoading] = useState(false);
  const navigate = useNavigate();

  const statusCopy = useMemo(() => {
    if (status === "verifying") return "Verifying your email...";
    if (status === "success")
      return redirecting
        ? `${message} Redirecting to your dashboard...`
        : message;
    return message;
  }, [status, message, redirecting]);

  useEffect(() => {
    let isMounted = true;
    const token = searchParams.get("token");
    if (!token) {
      setStatus("error");
      setMessage("No verification token provided");
      return;
    }

    const verifyEmail = async () => {
      try {
        await api.get(`/auth/verify-email?token=${token}`);

        if (!isMounted) return;
        setStatus("success");
        setMessage("Email verified successfully! You can now log in.");
        toast.success("Email verified successfully!");

        try {
          const me = await api.get("/auth/me");
          if (me?.success && me?.user) {
            setRedirecting(true);
            setTimeout(() => {
              if (isMounted) navigate("/");
            }, 1200);
          }
        } catch (authErr) {
          // Ignore auth check errors (likely not logged in)
          if (authErr?.silent) return;
        }
      } catch (err) {
        if (!isMounted) return;
        const errorMsg =
          err?.error ||
          err?.message ||
          "Verification failed. Token may be invalid or expired.";
        setStatus("error");
        setMessage(errorMsg);
        toast.error(errorMsg);
      }
    };

    verifyEmail();

    return () => {
      isMounted = false;
    };
  }, [searchParams, navigate]);

  const handleResend = async (e) => {
    e.preventDefault();
    if (!resendEmail.trim()) {
      toast.error("Please enter your email.");
      return;
    }
    setResendLoading(true);
    try {
      const res = await api.post("/auth/resend-verification", {
        email: resendEmail.trim(),
      });
      const msg =
        res?.message ||
        "If an account exists, a verification email has been sent.";
      toast.success(msg);
    } catch (err) {
      const errorMsg =
        err?.error || err?.message || "Unable to resend verification email.";
      toast.error(errorMsg);
    } finally {
      setResendLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-background py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-md w-full space-y-4">
        <Card
          className={`${
            status === "success"
              ? "bg-secondary/15"
              : status === "error"
              ? "bg-accent/15"
              : "bg-primary/10"
          }`}
        >
          <div
            className={`flex items-center gap-2 text-sm ${
              status === "success"
                ? "text-secondary"
                : status === "error"
                ? "text-accent"
                : "text-primary"
            }`}
          >
            {status === "verifying" && <LoadingSpinner size="sm" />}
            <span>{statusCopy}</span>
          </div>
        </Card>
        {status === "success" && (
          <div className="mt-4 text-center">
            <Link to="/login" className="text-primary hover:opacity-80">
              Go to Login
            </Link>
          </div>
        )}
        {status === "error" && (
          <Card className="p-4 space-y-3">
            <p className="text-sm text-text">
              Didn’t get the email? Resend the verification link.
            </p>
            <form onSubmit={handleResend} className="space-y-3">
              <Input
                label="Email"
                type="email"
                name="email"
                autoComplete="email"
                value={resendEmail}
                onChange={(e) => setResendEmail(e.target.value)}
                required
              />
              <Button type="submit" loading={resendLoading} className="w-full">
                Resend Verification Email
              </Button>
            </form>
          </Card>
        )}
      </div>
    </div>
  );
}
